import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, Eye, Ban, Check, DollarSign, Wallet, TrendingUp, Star, Gift, Coins, ArrowUpDown } from 'lucide-react';
import { adminApi, settingsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({ blcAmount: '', reason: '', wallet_type: 'cash_wallet' });
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  useEffect(() => {
    settingsApi.get().then(res => {
      setBlcPrice(res.data.settings?.blc_price || 0.027);
    }).catch(() => {});
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({ page, search, limit: 20 });
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await adminApi.toggleUserStatus(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await adminApi.verifyUser(userId);
      toast.success('User verified');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  const handleLoginAsUser = async (userId) => {
    try {
      const response = await adminApi.loginAsUser(userId);
      localStorage.setItem('admin_token', localStorage.getItem('token'));
      localStorage.setItem('token', response.data.token);
      window.open('/dashboard', '_blank');
      setTimeout(() => {
        localStorage.setItem('token', localStorage.getItem('admin_token'));
      }, 1000);
    } catch (error) {
      toast.error('Failed to login as user');
    }
  };

  const blcToUsd = (blc) => (parseFloat(blc) || 0) * blcPrice;
  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  const handleAdjustBalance = async () => {
    if (!adjustmentData.blcAmount || !adjustmentData.reason) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const res = await adminApi.adjustBalance({
        user_id: selectedUser.id,
        amount: parseFloat(adjustmentData.blcAmount),
        reason: adjustmentData.reason,
        wallet_type: adjustmentData.wallet_type,
        is_blc: true
      });
      toast.success(res.data.message);
      setShowAdjustModal(false);
      setAdjustmentData({ blcAmount: '', reason: '', wallet_type: 'cash_wallet' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust balance');
    }
  };

  // Wallet type options with icons
  const walletTypes = [
    { value: 'deposit_wallet', label: 'Deposit Wallet', icon: Wallet, description: 'For staking only' },
    { value: 'cash_wallet', label: 'Cash Wallet', icon: Coins, description: 'Withdrawable & stakeable' },
    { value: 'roi_wallet', label: 'ROI Wallet', icon: TrendingUp, description: 'Daily rewards' },
    { value: 'commission_wallet', label: 'Commission Wallet', icon: Users, description: 'Referral commissions' },
    { value: 'salary_wallet', label: 'Salary Wallet', icon: Star, description: 'Star program salary' },
    { value: 'promotion_wallet', label: 'Promotion Wallet', icon: Gift, description: 'Promotion rewards' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-500/10">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">User Management</h1>
            <p className="text-sm text-zinc-500">Manage all platform users</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name..."
            className="pl-10 bg-black/50 border-white/10"
            data-testid="user-search"
          />
        </div>
      </div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Wallets</th>
                  <th>Package</th>
                  <th>Team</th>
                  <th>Invested</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div>
                        <p className="text-white font-medium">{user.full_name}</p>
                        <p className="text-xs text-zinc-500">{user.email}</p>
                      </div>
                    </td>
                    <td>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Wallet className="w-3 h-3 text-green-400" />
                          <span className="text-xs text-zinc-400">Cash:</span>
                          <span className="text-xs text-white">${(user.cash_wallet || 0).toFixed(2)}</span>
                          <span className="text-[10px] text-amber-400">({usdToBLC(user.cash_wallet || 0)} BLC)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-3 h-3 text-amber-400" />
                          <span className="text-xs text-zinc-400">Deposit:</span>
                          <span className="text-xs text-white">${(user.deposit_wallet || 0).toFixed(2)}</span>
                          <span className="text-[10px] text-amber-400">({usdToBLC(user.deposit_wallet || 0)} BLC)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-3 h-3 text-blue-400" />
                          <span className="text-xs text-zinc-400">ROI:</span>
                          <span className="text-xs text-white">${(user.roi_wallet || 0).toFixed(2)}</span>
                          <span className="text-[10px] text-amber-400">({usdToBLC(user.roi_wallet || 0)} BLC)</span>
                        </div>
                      </div>
                    </td>
                    <td className="text-zinc-400">{user.current_package || '-'}</td>
                    <td>
                      <div>
                        <p className="text-white">{user.total_team}</p>
                        <p className="text-xs text-zinc-500">{user.direct_referrals} direct</p>
                      </div>
                    </td>
                    <td className="text-amber-400">${user.total_invested?.toFixed(2) || 0}</td>
                    <td>
                      <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {user.is_active ? 'Active' : 'Banned'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleLoginAsUser(user.id)}
                          className="p-1.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                          title="Login as user"
                          data-testid={`login-as-${user.id}`}
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowAdjustModal(true);
                          }}
                          className="p-1.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                          title="Adjust balance"
                        >
                          <DollarSign className="w-3 h-3" />
                        </button>
                        {!user.is_verified && (
                          <button
                            onClick={() => handleVerifyUser(user.id)}
                            className="p-1.5 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            title="Verify user"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className={`p-1.5 rounded ${
                            user.is_active 
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                              : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          }`}
                          title={user.is_active ? 'Ban user' : 'Activate user'}
                        >
                          <Ban className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-white/5">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  page === i + 1 ? 'bg-amber-500 text-black' : 'glass text-zinc-400'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </motion.div>

      {/* Adjust Balance Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Adjust Balance - {selectedUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* BLC Rate Info */}
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
              <span className="text-xs text-amber-400">Current BLC Rate</span>
              <span className="text-amber-400 font-bold">${blcPrice} per BLC</span>
            </div>

            {/* Current Balances */}
            {selectedUser && (
              <div className="p-4 rounded-lg bg-black/30 space-y-2">
                <p className="text-xs text-zinc-500 mb-2">Current Wallet Balances</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Cash:</span>
                    <span className="text-white">${(selectedUser.cash_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.cash_wallet || 0)} BLC)</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Deposit:</span>
                    <span className="text-white">${(selectedUser.deposit_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.deposit_wallet || 0)} BLC)</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">ROI:</span>
                    <span className="text-white">${(selectedUser.roi_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.roi_wallet || 0)} BLC)</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Commission:</span>
                    <span className="text-white">${(selectedUser.commission_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.commission_wallet || 0)} BLC)</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Salary:</span>
                    <span className="text-white">${(selectedUser.salary_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.salary_wallet || 0)} BLC)</span></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Promotion:</span>
                    <span className="text-white">${(selectedUser.promotion_wallet || 0).toFixed(2)} <span className="text-amber-400">({usdToBLC(selectedUser.promotion_wallet || 0)} BLC)</span></span>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Type Selection */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Select Wallet</label>
              <div className="grid grid-cols-2 gap-2">
                {walletTypes.map((wallet) => (
                  <button
                    key={wallet.value}
                    onClick={() => setAdjustmentData({...adjustmentData, wallet_type: wallet.value})}
                    className={`p-3 rounded-lg text-left transition-all ${
                      adjustmentData.wallet_type === wallet.value
                        ? 'bg-amber-500/20 border border-amber-500/50'
                        : 'bg-black/30 border border-transparent hover:border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <wallet.icon className={`w-4 h-4 ${adjustmentData.wallet_type === wallet.value ? 'text-amber-400' : 'text-zinc-400'}`} />
                      <span className={`text-sm font-medium ${adjustmentData.wallet_type === wallet.value ? 'text-white' : 'text-zinc-300'}`}>
                        {wallet.label}
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-500">{wallet.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* BLC Amount Input */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Amount in BLC (use negative to deduct)</label>
              <Input
                type="number"
                value={adjustmentData.blcAmount}
                onChange={(e) => setAdjustmentData({...adjustmentData, blcAmount: e.target.value})}
                placeholder="e.g., 1000 or -500"
                className="bg-black/50 border-white/10"
                data-testid="adjust-blc-amount"
              />
              {adjustmentData.blcAmount && (
                <div className="mt-2 p-3 rounded-lg bg-black/40 border border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">USD Equivalent:</span>
                    <span className={`text-sm font-bold ${parseFloat(adjustmentData.blcAmount) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {parseFloat(adjustmentData.blcAmount) >= 0 ? '+' : ''}${blcToUsd(adjustmentData.blcAmount).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-zinc-500">BLC Amount:</span>
                    <span className="text-sm font-bold text-amber-400">
                      {parseFloat(adjustmentData.blcAmount) >= 0 ? '+' : ''}{parseFloat(adjustmentData.blcAmount || 0).toFixed(2)} BLC
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Reason (will be shown to user)</label>
              <Input
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
                placeholder="e.g., Bonus reward, Manual correction..."
                className="bg-black/50 border-white/10"
                data-testid="adjust-reason"
              />
            </div>

            <Button onClick={handleAdjustBalance} className="w-full gold-btn" data-testid="apply-adjustment-btn">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              Apply BLC Adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
